import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { ApexReference } from './ApexReference';

export default function QuickCloseJobModal({ appointment, onClose, onSuccess }) {
  const isWarranty = Boolean(appointment.warranty_parent_id);
  const [workCompleted, setWorkCompleted] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [workType, setWorkType] = useState(appointment.work_type || 'onsite');
  const [startTime, setStartTime] = useState(appointment.start_time);
  const [endTime, setEndTime] = useState(appointment.end_time);
  const [loading, setLoading] = useState(false);
  const [entryMode, setEntryMode] = useState('times'); // 'times' or 'hours'
  const [billableHours, setBillableHours] = useState('');
  const [paymentType, setPaymentType] = useState(''); // 'cash' or 'invoice'
  const [customerEmail, setCustomerEmail] = useState(appointment.customer_email || '');
  const [customerMobile, setCustomerMobile] = useState(appointment.customer_mobile || '');

  const handleClose = async (e) => {
    e.preventDefault();
    
    if (!workCompleted.trim()) {
      toast.error('Please add what work was completed');
      return;
    }

    if (entryMode === 'hours' && (!billableHours || parseFloat(billableHours) <= 0)) {
      toast.error(isWarranty ? 'Please enter hours worked' : 'Please enter billable hours');
      return;
    }

    // Validate payment type
    if (!isWarranty && !paymentType) {
      toast.error('Please select payment type (Cash or Invoice)');
      return;
    }

    // Validate invoice requirements
    if (!isWarranty && paymentType === 'invoice') {
      if (!customerEmail && !customerMobile) {
        toast.error('For invoice jobs, please provide email and/or mobile number');
        return;
      }
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        status: 'Closed',
        work_completed: workCompleted,
        completion_notes: completionNotes.trim() || workCompleted,
        work_type: workType,
        start_time: startTime,
        end_time: endTime,
        payment_type: isWarranty ? 'warranty' : paymentType,
        customer_email: customerEmail || null,
        customer_mobile: customerMobile || null
      };

      // Add billable hours if in hours mode
      if (entryMode === 'hours') {
        updateData.actual_hours = parseFloat(billableHours);
      }

      await axios.put(
        `${API}/appointments/${appointment.id}/user-update`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Job closed! Work entry created automatically.');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="close-job-modal" className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center p-4 z-50 overflow-y-auto job-modal">
      <div className="retro-panel max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
        <div className="mb-4"><ApexReference job={appointment} scope="close" /></div>
        <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 glow-text" style={{ color: '#00FF00' }}>
          {isWarranty ? '[CLOSE WARRANTY TICKET]' : '[CLOSE JOB]'}
        </h2>

        {/* Job Info */}
        <div className="mb-4 md:mb-6 p-3 md:p-4 border-2" style={{ borderColor: '#00FF00' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm" style={{ color: '#ffffff' }}>
            <div><strong style={{ color: '#00FF00' }}>Customer:</strong> {appointment.customer_name}</div>
            <div><strong style={{ color: '#00FF00' }}>Job Type:</strong> {appointment.job_type}</div>
            <div><strong style={{ color: '#00FF00' }}>Date:</strong> {appointment.appointment_date}</div>
            <div><strong style={{ color: '#00FF00' }}>Time:</strong> {appointment.start_time} - {appointment.end_time}</div>
          </div>
        </div>

        <form onSubmit={handleClose} className="space-y-4 md:space-y-6">
          {isWarranty && <p data-testid="close-job-warranty-billing" className="text-cyan-300">Warranty ticket · Non-billable</p>}
          {/* Entry Mode Toggle - Visible on ALL devices */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#00D4FF' }}>
              [TIME ENTRY METHOD]:
            </label>
            <div className="flex gap-2 sm:gap-4">
              <button
                type="button"
                onClick={() => setEntryMode('times')}
                data-testid="close-job-time-mode"
                className={`retro-btn flex-1 text-sm sm:text-base ${entryMode === 'times' ? 'retro-btn-cyan' : ''}`}
                style={{ 
                  backgroundColor: entryMode === 'times' ? '#00D4FF' : undefined,
                  color: entryMode === 'times' ? '#000' : '#00D4FF',
                  borderColor: '#00D4FF'
                }}
              >
                [START/STOP TIMES]
              </button>
              <button
                type="button"
                onClick={() => setEntryMode('hours')}
                data-testid="close-job-hours-mode"
                className={`retro-btn flex-1 text-sm sm:text-base ${entryMode === 'hours' ? 'retro-btn-cyan' : ''}`}
                style={{ 
                  backgroundColor: entryMode === 'hours' ? '#00D4FF' : undefined,
                  color: entryMode === 'hours' ? '#000' : '#00D4FF',
                  borderColor: '#00D4FF'
                }}
              >
                {isWarranty ? '[HOURS WORKED]' : '[BILLABLE HOURS]'}
              </button>
            </div>
          </div>

          {/* Start and Stop Times - Only show if times mode */}
          {entryMode === 'times' && (
            <div>
              <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
                [ACTUAL WORK TIMES]:
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm" style={{ color: '#FF6B00' }}>
                    Start Time:
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    data-testid="close-job-start-time"
                    onChange={(e) => setStartTime(e.target.value)}
                    className="retro-input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm" style={{ color: '#FF6B00' }}>
                    Stop Time:
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    data-testid="close-job-end-time"
                    onChange={(e) => setEndTime(e.target.value)}
                    className="retro-input w-full"
                    required
                  />
                </div>
              </div>
              <div className="mt-2 text-xs" style={{ color: 'rgba(255, 107, 0, 0.7)' }}>
                ℹ️ Adjust times if different from scheduled. These will be used for the work entry.
              </div>
            </div>
          )}

          {/* Billable Hours - Only show if hours mode */}
          {entryMode === 'hours' && (
            <div>
              <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
                {isWarranty ? '[HOURS WORKED]:' : '[BILLABLE HOURS]:'}
              </label>
              <input
                type="number"
                step="0.25"
                min="0"
                value={billableHours}
                data-testid="close-job-hours"
                onChange={(e) => setBillableHours(e.target.value)}
                className="retro-input w-full"
                placeholder="e.g., 2.5"
                required
              />
              <div className="mt-2 text-xs" style={{ color: 'rgba(255, 107, 0, 0.7)' }}>
                {isWarranty ? 'Non-billable time' : 'Total billable hours'}
              </div>
            </div>
          )}

          {/* Work Type */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#00D4FF' }}>
              [WORK TYPE]:
            </label>
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <button
                type="button"
                onClick={() => setWorkType('onsite')}
                data-testid="close-job-onsite"
                className={`retro-btn ${workType === 'onsite' ? 'retro-btn-green' : ''}`}
                style={{ 
                  backgroundColor: workType === 'onsite' ? '#00FF00' : undefined,
                  color: workType === 'onsite' ? '#000' : '#00D4FF',
                  borderColor: '#00D4FF'
                }}
              >
                [ONSITE]
              </button>
              <button
                type="button"
                onClick={() => setWorkType('remote')}
                data-testid="close-job-remote"
                className={`retro-btn ${workType === 'remote' ? 'retro-btn-yellow' : ''}`}
                style={{ 
                  backgroundColor: workType === 'remote' ? '#FFD60A' : undefined,
                  color: workType === 'remote' ? '#000' : '#00D4FF',
                  borderColor: '#00D4FF'
                }}
              >
                [REMOTE]
              </button>
              <button
                type="button"
                onClick={() => setWorkType('offsite')}
                data-testid="close-job-offsite"
                className={`retro-btn ${workType === 'offsite' ? 'retro-btn-cyan' : ''}`}
                style={{ 
                  backgroundColor: workType === 'offsite' ? '#00D4FF' : undefined,
                  color: workType === 'offsite' ? '#000' : '#00D4FF',
                  borderColor: '#00D4FF'
                }}
              >
                [OFFSITE]
              </button>
            </div>
            <div className="mt-2 text-xs" style={{ color: 'rgba(0, 212, 255, 0.7)' }}>
              ℹ️ OFFSITE = No time limits for this job entry
            </div>
          </div>

          {/* Payment Type - REQUIRED */}
          {!isWarranty && <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#FFD60A' }}>
              [PAYMENT TYPE] <span className="text-red-500">*</span>:
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setPaymentType('cash')}
                data-testid="close-job-cash"
                className={`retro-btn flex-1 ${paymentType === 'cash' ? 'retro-btn-yellow' : ''}`}
                style={{ 
                  backgroundColor: paymentType === 'cash' ? '#FFD60A' : undefined,
                  color: paymentType === 'cash' ? '#000' : '#FFD60A',
                  borderColor: '#FFD60A'
                }}
              >
                [CASH]
              </button>
              <button
                type="button"
                onClick={() => setPaymentType('invoice')}
                data-testid="close-job-invoice"
                className={`retro-btn flex-1 ${paymentType === 'invoice' ? 'retro-btn-yellow' : ''}`}
                style={{ 
                  backgroundColor: paymentType === 'invoice' ? '#FFD60A' : undefined,
                  color: paymentType === 'invoice' ? '#000' : '#FFD60A',
                  borderColor: '#FFD60A'
                }}
              >
                [INVOICE]
              </button>
            </div>
          </div>}

          {/* Customer Contact - Only show if Invoice selected */}
          {!isWarranty && paymentType === 'invoice' && (
            <div className="border-2 border-yellow-500 p-4 rounded">
              <div className="text-yellow-500 font-bold mb-3 text-sm">
                📧 INVOICE CONTACT DETAILS (Email and/or Mobile required)
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block mb-1 text-sm" style={{ color: '#FFD60A' }}>
                    Customer Email:
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    data-testid="close-job-email"
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="retro-input w-full"
                    placeholder="customer@example.com"
                    style={{ borderColor: '#FFD60A' }}
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm" style={{ color: '#FFD60A' }}>
                    Customer Mobile:
                  </label>
                  <input
                    type="tel"
                    value={customerMobile}
                    data-testid="close-job-mobile"
                    onChange={(e) => setCustomerMobile(e.target.value)}
                    className="retro-input w-full"
                    placeholder="04XX XXX XXX"
                    style={{ borderColor: '#FFD60A' }}
                  />
                </div>
              </div>

              <div className="mt-2 text-xs" style={{ color: 'rgba(255, 214, 10, 0.7)' }}>
                ℹ️ At least one contact method required for invoice jobs
              </div>
            </div>
          )}

          {/* Work Completed */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#00FF00' }}>
              [WORK COMPLETED] *:
            </label>
            <textarea
              value={workCompleted}
              data-testid="close-job-work-completed"
              onChange={(e) => setWorkCompleted(e.target.value)}
              className="retro-input w-full"
              rows="5"
              placeholder="What work did you complete?&#10;&#10;Example:&#10;- Installed new router&#10;- Configured network settings&#10;- Tested connectivity&#10;- Customer approved"
              style={{ borderColor: '#00FF00' }}
              required
            />
          </div>

          {/* Optional Completion Notes */}
          <div>
            <label className="block mb-2 text-lg font-bold" style={{ color: '#FF6B00' }}>
              [ADDITIONAL NOTES] (optional):
            </label>
            <textarea
              value={completionNotes}
              data-testid="close-job-completion-notes"
              onChange={(e) => setCompletionNotes(e.target.value)}
              className="retro-input w-full"
              rows="2"
              placeholder="Any additional information..."
            />
          </div>

          {/* Info Box */}
          <div className="p-3 border-2" style={{ borderColor: '#00FF00', backgroundColor: '#001a00' }}>
            <div className="text-sm" style={{ color: '#00FF00' }}>
              ✓ Job will be marked as CLOSED<br />
              ✓ Work entry will be created automatically<br />
              ✓ Times from job schedule will be recorded
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              data-testid="close-job-submit"
              className="retro-btn retro-btn-green flex-1"
              disabled={loading}
            >
              {loading ? '[CLOSING JOB...]' : '[CLOSE JOB & CREATE ENTRY]'}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="close-job-cancel"
              className="retro-btn retro-btn-magenta flex-1"
              disabled={loading}
            >
              [CANCEL]
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
